import {
  ColumnVisibilityState,
  ColumnSizingState,
  ColumnPinningState,
  ColumnOrderState,
  RowPinningState,
} from "@tanstack/react-table";
import {
  useState, useEffect, useMemo, type Dispatch, type SetStateAction,
} from "react";
import type { TanStackColumn } from "../types";
import type { RowData } from "@tanstack/react-table";

const getColumnId = <T extends RowData>(column: TanStackColumn<T>, fallbackIndex?: number): string => {
  const accessorKey = (column as { accessorKey?: string }).accessorKey;
  return column.id || accessorKey || (fallbackIndex !== undefined ? `column-${fallbackIndex}` : "");
};

/**
 * Generic hook for managing state persisted to localStorage.
 *
 * @param keyPrefix - The prefix for the localStorage key (e.g., "columnSizing")
 * @param storageKey - Optional unique identifier to scope the storage (appended to prefix)
 * @param defaultValue - Default value when nothing is in storage
 * @param parse - Optional conversion of the stored value (e.g. an older format)
 * @returns Tuple of [state, setState] similar to useState
 */
export function useLocalStorageState<T>(
  keyPrefix: string,
  storageKey: string | undefined,
  defaultValue: T,
  parse: (stored: unknown) => T = (stored) => stored as T,
): [T, Dispatch<SetStateAction<T>>] {
  const fullKey = storageKey ? `${keyPrefix}-${storageKey}` : null;

  const [state, setState] = useState<T>(() => {
    if (fullKey) {
      const saved = localStorage.getItem(fullKey);
      if (saved) {
        try {
          return parse(JSON.parse(saved));
        }
        catch {
          // Ignore parsing errors
        }
      }
    }
    return defaultValue;
  });

  useEffect(() => {
    if (fullKey) {
      localStorage.setItem(fullKey, JSON.stringify(state));
    }
  }, [state, fullKey]);

  return [state, setState];
}

// Custom hook to manage column visibility with localStorage
export const useColumnVisibilityStorage = (
  storageKey?: string,
  defaultHiddenColumns?: string[],
) => {
  const [columnVisibility, setColumnVisibility] = useLocalStorageState<ColumnVisibilityState>(
    "columnVisibility",
    storageKey,
    {},
  );

  // Memoize the stringified representation for stable dependency comparison
  const defaultHiddenColumnsKey = useMemo(() => {
    return defaultHiddenColumns?.join(",") || "";
  }, [defaultHiddenColumns]);

  // Apply default hidden columns on every render to ensure new custom attributes are hidden
  useEffect(() => {
    if (defaultHiddenColumns && defaultHiddenColumns.length > 0) {
      setColumnVisibility((prev) => {
        const updated = { ...prev };
        let hasChanges = false;

        defaultHiddenColumns.forEach((colId) => {
          // Set to false if not already explicitly set
          if (updated[colId] === undefined) {
            updated[colId] = false;
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }
  }, [defaultHiddenColumnsKey, defaultHiddenColumns, setColumnVisibility]);

  return { columnVisibility, setColumnVisibility };
};

// Custom hook to manage column sizing with localStorage
export const useColumnSizingStorage = (storageKey?: string) => {
  const [columnSizing, setColumnSizing] = useLocalStorageState<ColumnSizingState>(
    "columnSizing",
    storageKey,
    {},
  );

  return { columnSizing, setColumnSizing };
};

// react-table 8 enregistrait l'épinglage en left/right, la v9 en start/end.
const parseColumnPinning = (stored: unknown): ColumnPinningState => {
  const pinning = stored as ColumnPinningState & { left?: string[], right?: string[] };
  return { start: pinning.start ?? pinning.left ?? [], end: pinning.end ?? pinning.right ?? [] };
};

// Custom hook to manage column pinning with localStorage
export const useColumnPinningStorage = (
  storageKey?: string,
  initialPinning?: ColumnPinningState,
) => {
  const [columnPinning, setColumnPinning] = useLocalStorageState<ColumnPinningState>(
    "columnPinning",
    storageKey,
    initialPinning || { start: [], end: [] },
    parseColumnPinning,
  );

  return { columnPinning, setColumnPinning };
};

// Custom hook to manage column ordering with localStorage
export const useColumnOrderStorage = (
  storageKey?: string,
  initialOrder?: ColumnOrderState,
) => {
  const [columnOrder, setColumnOrder] = useLocalStorageState<ColumnOrderState>(
    "columnOrder",
    storageKey,
    initialOrder || [],
  );

  return { columnOrder, setColumnOrder };
};

// Custom hook to manage row pinning with localStorage
export const useRowPinningStorage = (
  storageKey?: string,
  initialPinning?: RowPinningState,
) => {
  const [rowPinning, setRowPinning] = useLocalStorageState<RowPinningState>(
    "rowPinning",
    storageKey,
    initialPinning || { top: [], bottom: [] },
  );

  return { rowPinning, setRowPinning };
};

// Utility function to synchronize localStorage column order with runtime columns
const synchronizeColumnOrder = (
  storageKey: string,
  runtimeColumns: string[],
  initialOrder?: ColumnOrderState,
): ColumnOrderState => {
  const savedOrder = localStorage.getItem(`columnOrder-${storageKey}`);
  let currentOrder: string[] = [];

  if (savedOrder) {
    try {
      currentOrder = JSON.parse(savedOrder) as string[];
    }
    catch {
      // If parsing fails, use runtime columns as fallback
      currentOrder = runtimeColumns;
    }
  }
  else if (initialOrder) {
    currentOrder = initialOrder;
  }
  else {
    currentOrder = runtimeColumns;
  }

  // Create synchronized order:
  // 1. Start with columns that exist in both saved order and runtime
  // 2. Add any new runtime columns that aren't in saved order
  // 3. Remove any saved columns that don't exist in runtime
  const validSavedColumns = currentOrder.filter((id) =>
    runtimeColumns.includes(id),
  );
  const newRuntimeColumns = runtimeColumns.filter(
    (id) => !currentOrder.includes(id),
  );

  // Separate new custom attribute columns from other new columns
  const newCustomAttributeColumns = newRuntimeColumns.filter((id) =>
    id.startsWith("custom_attribute_"),
  );
  // ID columns (employee_id, representative_id, beneficiary_id) should be placed right after "select"
  const ID_COLUMNS = new Set(["employee_id", "representative_id", "beneficiary_id"]);
  const newIdColumns = newRuntimeColumns.filter((id) => ID_COLUMNS.has(id));
  const otherNewColumns = newRuntimeColumns.filter(
    (id) => !id.startsWith("custom_attribute_") && id !== "select" && !newIdColumns.includes(id),
  );

  // "select" column always goes first
  const hasSelectColumn = runtimeColumns.includes("select");

  // Collect existing ID columns already in saved order (not new) that should stay after select
  const existingIdColumns = validSavedColumns.filter((id) =>
    ID_COLUMNS.has(id) && id !== "select",
  );

  // Remove "select" and all ID columns from validSavedColumns (will be re-added at the start)
  const fixedStartColumns = new Set(["select", ...newIdColumns, ...existingIdColumns]);
  const validSavedColumnsFiltered = validSavedColumns.filter((id) => !fixedStartColumns.has(id));

  // Find the index of the actions column in the valid saved columns
  const actionsIndex = validSavedColumnsFiltered.findIndex((id) => id === "actions");

  let synchronizedOrder: string[];

  if (actionsIndex !== -1) {
    // Insert new columns before actions column
    synchronizedOrder = [
      ...validSavedColumnsFiltered.slice(0, actionsIndex),
      ...otherNewColumns,
      ...newCustomAttributeColumns,
      ...validSavedColumnsFiltered.slice(actionsIndex),
    ];
  }
  else {
    // No actions column found, add at the end
    synchronizedOrder = [
      ...validSavedColumnsFiltered,
      ...otherNewColumns,
      ...newCustomAttributeColumns,
    ];
  }

  // Ensure "select" is always first, followed by ID columns
  const allIdColumns = [...existingIdColumns, ...newIdColumns];
  if (hasSelectColumn) {
    synchronizedOrder = ["select", ...allIdColumns, ...synchronizedOrder];
  }
  else if (allIdColumns.length > 0) {
    synchronizedOrder = [...allIdColumns, ...synchronizedOrder];
  }

  // Only update localStorage if the order has changed (efficient array comparison)
  const hasOrderChanged =
    synchronizedOrder.length !== currentOrder.length ||
    synchronizedOrder.some((id, index) => id !== currentOrder[index]);

  if (hasOrderChanged) {
    localStorage.setItem(
      `columnOrder-${storageKey}`,
      JSON.stringify(synchronizedOrder),
    );
  }

  return synchronizedOrder;
};

const synchronizeColumnSizing = <T extends RowData>(
  currentSizing: ColumnSizingState,
  columns: TanStackColumn<T>[],
): ColumnSizingState => {
  const runtimeBounds = new Map<string, { min: number, max: number }>();

  columns.forEach((column, index) => {
    const id = getColumnId(column, index);
    if (!id) return;

    const min = column.minSize ?? (column.isSelectionColumn ? 60 : 120);
    const max = column.maxSize ?? (column.canWidthGrow ? 1000 : 400);
    runtimeBounds.set(id, { min, max });
  });

  const nextSizing: ColumnSizingState = {};

  Object.entries(currentSizing).forEach(([columnId, width]) => {
    const bounds = runtimeBounds.get(columnId);
    if (!bounds) return;

    const numericWidth = Number(width);
    if (!Number.isFinite(numericWidth)) return;

    const clamped = Math.min(bounds.max, Math.max(bounds.min, numericWidth));
    nextSizing[columnId] = clamped;
  });

  return nextSizing;
};

// Custom hook for table state management
export const useTableStateStorage = <T extends RowData>({
  storageKey,
  initialColumnPinning,
  initialColumnOrder,
  initialRowPinning,
  columns,
}: {
  storageKey?: string
  initialColumnPinning?: ColumnPinningState
  initialColumnOrder?: ColumnOrderState
  initialRowPinning?: RowPinningState
  columns: TanStackColumn<T>[]
}) => {
  // Calculate default hidden columns (custom attributes)
  const defaultHiddenColumns = useMemo(() => {
    return columns
      .filter((col) => col.isCustomAttribute || col.defaultHidden)
      .map((col, index) => getColumnId(col, index))
      .filter(Boolean);
  }, [columns]);

  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStorage(
    storageKey,
    defaultHiddenColumns,
  );
  const { columnSizing, setColumnSizing } = useColumnSizingStorage(storageKey);
  const { columnPinning, setColumnPinning } = useColumnPinningStorage(
    storageKey,
    initialColumnPinning,
  );

  // Calculate runtime column IDs from current columns
  const runtimeColumnIds = useMemo(() => {
    return columns
      .map((column, index) => getColumnId(column, index))
      .filter(Boolean);
  }, [columns]);

  // Synchronize localStorage with runtime columns
  const runtimeColumnsKey = useMemo(() => runtimeColumnIds.join(","), [runtimeColumnIds]);
  const synchronizedColumnOrder = useMemo(() => {
    if (!storageKey) return initialColumnOrder || runtimeColumnIds;
    return synchronizeColumnOrder(
      storageKey,
      runtimeColumnIds,
      initialColumnOrder,
    );
  }, [storageKey, runtimeColumnsKey, initialColumnOrder]);

  const { columnOrder, setColumnOrder } = useColumnOrderStorage(
    storageKey,
    synchronizedColumnOrder,
  );
  const { rowPinning, setRowPinning } = useRowPinningStorage(
    storageKey,
    initialRowPinning,
  );

  const columnsKey = useMemo(() => {
    return columns.map((column, index) => {
      const id = getColumnId(column, index);
      const min = column.minSize ?? (column.isSelectionColumn ? 60 : 120);
      const max = column.maxSize ?? (column.canWidthGrow ? 1000 : 400);
      return `${id}:${min}:${max}`;
    }).join("|");
  }, [columns]);

  // Memoize comparison to avoid expensive JSON.stringify operations
  const ordersAreDifferent = useMemo(() => {
    if (!synchronizedColumnOrder) return false;
    const currentSet = new Set(columnOrder);
    const syncSet = new Set(synchronizedColumnOrder);
    const sameSize = currentSet.size === syncSet.size;
    const sameMembers = sameSize && Array.from(currentSet).every((id) => syncSet.has(id));
    return !sameMembers;
  }, [columnOrder, synchronizedColumnOrder]);

  // Force update columnOrder when synchronizedColumnOrder changes
  useEffect(() => {
    if (synchronizedColumnOrder && ordersAreDifferent) {
      setColumnOrder(synchronizedColumnOrder);
    }
  }, [synchronizedColumnOrder, ordersAreDifferent, setColumnOrder]);

  // Ensure "select" column is always visible when present in runtime columns
  const hasRuntimeSelectColumn = runtimeColumnIds.includes("select");
  useEffect(() => {
    if (hasRuntimeSelectColumn) {
      setColumnVisibility((prev) => {
        if (prev.select === false) {
          const { select: _, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    }
  }, [hasRuntimeSelectColumn, setColumnVisibility]);

  useEffect(() => {
    const syncedSizing = synchronizeColumnSizing(columnSizing, columns);
    const prevEntries = Object.entries(columnSizing);
    const nextEntries = Object.entries(syncedSizing);

    const isSame =
      prevEntries.length === nextEntries.length
      && prevEntries.every(([key, value]) => syncedSizing[key] === value);

    if (!isSame) {
      setColumnSizing(syncedSizing);
    }
  }, [columnSizing, columnsKey, columns, setColumnSizing]);

  return {
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
    columnPinning,
    setColumnPinning,
    columnOrder,
    setColumnOrder,
    rowPinning,
    setRowPinning,
  };
};
